import { Box, Theme, darken, useTheme } from '@mui/material';
import clsx from 'clsx';
import React, { CSSProperties, KeyboardEventHandler, MouseEventHandler, Ref } from 'react';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { isDifferentWebsite, retrievePath } from '../../lib/iris';
import { headingCID } from '../Heading';

import ExternalLink from './ExternalLink';

export const linkActiveCID = 'CID-LinkActive';
export const HOVER_COEFFICIENT = 0.3;

export type IsActiveCheck = (to: string) => boolean;

export interface LinkProps {
  activeClassName?: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  bold?: boolean;
  id?: string;
  isActive?: IsActiveCheck;
  isIndex?: boolean;
  onClick?: MouseEventHandler<Element>;
  onKeyUp?: KeyboardEventHandler<Element>;
  role?: string;
  style?: CSSProperties;
  tabIndex?: number;
  target?: '_blank';
  title?: string;
  to: string;
}

interface PropTypesWithRef extends LinkProps {
  innerRef: Ref<HTMLAnchorElement>;
}

const useIsActive = (to: string, isActiveCheck?: IsActiveCheck) => {
  const location = useLocation();

  if (isActiveCheck) {
    return isActiveCheck(to);
  }

  const relative = retrievePath(to);
  const sameOrigin = !isDifferentWebsite(to);

  return sameOrigin && relative === location.pathname + location.search + location.hash;
};

const styles = (theme: Theme, props: PropTypesWithRef) => ({
  [`&:hover .${headingCID}`]: {
    color: darken(theme.palette.link?.header || theme.palette.common.black, HOVER_COEFFICIENT),
  },
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  fontWeight: props.bold ? 600 : undefined,
  display: 'inline-flex',
  whiteSpace: 'pre-wrap',
});

const Link = (props: PropTypesWithRef): JSX.Element => {
  const {
    activeClassName,
    bold,
    children,
    className = 'Link',
    disabled,
    innerRef,
    isActive: isActiveCheck,
    isIndex = true,
    onClick,
    target,
    title,
    to,
    ...other
  } = props;
  const theme = useTheme();
  const active = useIsActive(to, isActiveCheck);

  if (disabled) {
    return (
      <Box disabled className={className} component="button" sx={styles(theme, props)} title={title} type="button">
        {children}
      </Box>
    );
  }

  if (isDifferentWebsite(to)) {
    return (
      <Box
        className={className}
        component={ExternalLink}
        href={to}
        id={other.id}
        ref={innerRef}
        role={other.role}
        sx={styles(theme, props)}
        tabIndex={other.tabIndex}
        onClick={onClick}
      >
        {children}
      </Box>
    );
  }

  const path = retrievePath(to);
  const linkClassName = clsx({
    [className]: true,
    [activeClassName || linkActiveCID]: active,
  });

  return (
    <Box
      {...other}
      className={linkClassName}
      component={NavLink}
      end={isIndex}
      ref={innerRef}
      sx={styles(theme, props)}
      target={target}
      title={title}
      to={path ?? ''}
      onClick={onClick}
    >
      {children}
    </Box>
  );
};

export default React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => <Link innerRef={ref} {...props} />);
